<template>
  <div class="c-template-management w-full h-full">
    <CBulkToolbar
      v-show="showBulkToolbar"
      class="min-h[68px] px-5 mb-4"
      :class="{
        '!min-h[114px]': isTemplateFolderEnabled,
      }"
      :count="bulkToolbarCount"
      :disabled="isBulkToolbarDisabled"
      :moreActions="{
        addTags: !isTemplateFolderEnabled ? true : selectedTemplateIds.length === selectedItemIds.length,
        removeTags: !isTemplateFolderEnabled ? true : selectedTemplateIds.length === selectedItemIds.length,
        delete: !selectedFolderIds?.length,
        manageAttributes: showManageAttributes,
        manageDistribution: isAdmin && isDistributionEnabled && props.canvasType !== 'section-template',
      }"
      :selectedCount="selectedItemIds.length"
      :showDelete="!selectedFolderIds?.length"
      :showMore="!isTemplateFolderEnabled ? true : isTemplateFolderEnabled && !selectedFolderIds?.length"
      :showMove="isTemplateFolderEnabled && isAdmin"
      @addTags="bulkAddTags"
      @delete="bulkDelete"
      @manageAttributes="bulkUpdateMetadata"
      @manageDistribution="onBulkDistribution"
      @move="onMove(undefined, { isBulk: true })"
      @removeTags="bulkRemoveTags"
      @toggleSelect="toggleSelectAll"
    />
    <CTemplateManagementToolbar
      v-show="!showBulkToolbar"
      ref="toolbarWrapper"
      :breadcrumbs="breadcrumbs"
      :canvas-type="canvasType"
      :class="{ 'mb-4': isTemplateFolderEnabled }"
      :currentSort="currentSort"
      :custom-filter-components="dataFilterComponents"
      :excluded-tags="excludedTags"
      :hide-convert="isImpact || hideConvert"
      :hide-create="isImpact"
      :hide-view="!launchDarkly.allow_canvases_tables_columns_settings"
      :is-loading-items="isLoading"
      :is-template-folder-enabled="isTemplateFolderEnabled"
      :metadata-filter-values="filters"
      :recommended-ids="activeRecommendedIds"
      :search="search"
      :tableColumnsSettings="tableColumnsSettings"
      :viewMode="viewMode"
      @breadcrumb:click="fetchByParentId"
      @convert="emit('convert')"
      @create:folder="showFolderModal = true"
      @create:template="emit('create', isTemplateFolderEnabled ? currentFolderId : undefined)"
      @move="onColumnsMove"
      @restoreColumns="restoreColumns"
      @search="onTableChange('searchChanged', $event)"
      @select="onColumnsSelect"
      @sort-changed="onSortChanged"
      @toggle-view-mode="toggleViewMode"
      @update:filter-values="onTableChange('filterValuesChanged', $event)"
    />
    <div
      v-if="(isFetchingData || isLoadingTable) && isTemplateFolderEnabled && viewMode === 'list'"
      class="flex bg-base rounded-2 mr-3"
      :style="{ height: containerHeight }"
    >
      <div class="w-full pa-2">
        <NSkeleton v-for="n in 5" :key="n" class="rounded" :class="{ 'mt-1': n > 1 }" :height="120" width="100%" />
      </div>
    </div>
    <CList
      v-else-if="isTemplateFolderEnabled && viewMode === 'list'"
      ref="listRef"
      class="rounded-2 flex bg-base"
      :data="filteredAndSortedData"
      :height="containerHeight"
      :max-row-height="120"
      :movable="isAdmin"
      :selectedIds="selectedItemIds"
      @item:click="onOpenFile"
      @move="(event) => handleMoveItems(event.targetItemId, event.itemIds, { isDropAction: true })"
    >
      <template #item="{ item, isDraggedOver, isSelected, isContextMenuOpen }">
        <CTemplateManagementListItem
          :highlight-text="search"
          :is-context-menu-open="isContextMenuOpen"
          :is-dragged-over="isDraggedOver"
          :is-editing-current-item="isEditingCurrentItem"
          :is-readonly="isReadonly(toListItem(item))"
          :is-selected="isSelected"
          :item="item"
          :popularity-dict="popularityDict"
          :recommended-section-ids="recommendedIds"
          :selected-item-ids="selectedItemIds.map((id) => id.toString())"
          @edit="onEdit"
          @toggle-item-selection="toggleItemSelection"
          @update-name="onUpdateName"
          @update-tags="onUpdateTags"
        >
          <template #actions>
            <CTemplateManagementActionsCell
              :canvas-id="item.id.toString()"
              :hide-convert="isImpact || hideConvert"
              :hide-copy="isImpact || isFolder(toListItem(item))"
              :hide-create="isImpact"
              :hide-create-from-template="
                isAdmin || !!item.is_draft || isFolder(toListItem(item)) || hasSystemOrExcludedTag(item.tags)
              "
              :hide-delete="isReadonly(toListItem(item))"
              :hide-edit="isReadonly(toListItem(item)) || isFolder(toListItem(item))"
              :hide-lock="
                isFolder(toListItem(item)) ||
                !launchDarkly.enable_canvas_locks ||
                (isOrgAdmin ? isImpact : isLockedBySomeoneElse(toListItem(item)) || isImpact)
              "
              :hide-move="isImpact"
              :hide-toggle-default="
                isImpact ||
                isFolder(toListItem(item)) ||
                item.is_draft ||
                canvasType === 'section-template' ||
                (hasHomeOrShareTag(item.tags) && !item.is_default)
              "
              :hide-toggle-status="isFolder(toListItem(item)) || isReadonly(toListItem(item))"
              :hide-view="isFolder(toListItem(item))"
              :hide-view-details="isImpact || isFolder(toListItem(item))"
              :hide-view-new-tab="isPitcherWkWebView || isFolder(toListItem(item))"
              :is-copying="isCopyingId === item.id"
              :is-default="!!toListItem(item).is_default"
              :is-deleting="deletingInProgress.has(item.id.toString())"
              :is-draft="!!toListItem(item).is_draft"
              @copy="() => onCopy(item as CanvasRetrieve)"
              @create-from-template="() => emit('createFromTemplate', item.id.toString())"
              @delete="() => deleteItem(item as CanvasRetrieve)"
              @edit="() => emit('edit', item as CanvasRetrieve)"
              @lock="() => lockCanvas(item.id.toString()).then(() => fetchByParentId(currentFolderId))"
              @move="() => onMove(item as CanvasRetrieve)"
              @toggle-default="() => onToggleDefault(item as CanvasRetrieve)"
              @toggle-status="
              () => onUpdateStatus(item as CanvasRetrieve, item.is_draft ? CanvasStatus.READY : CanvasStatus.DRAFT)
            "
              @unlock="
                () => unlockCanvas(getCanvasLock(item.id.toString())?.id).then(() => fetchByParentId(currentFolderId))
              "
              @view="() => emit('open', item as CanvasRetrieve)"
              @view-details="() => emit('details', item as CanvasRetrieve)"
              @view-new-tab="() => emit('open', item as CanvasRetrieve, true)"
            />
          </template>
        </CTemplateManagementListItem>
      </template>
    </CList>
    <CTable
      v-else
      ref="tableRef"
      v-model:selected-rows="selectedRows"
      class="mt-3"
      :columns="launchDarkly.allow_canvases_tables_columns_settings ? columnsFromSettings : defaultColumns"
      :data="filteredAndSortedData"
      :disabled-ids="locks?.filter((lock) => !isLockedByCurrentUser(lock.canvas_id)).map((lock) => lock.canvas_id)"
      :initial-sorting="tableSorting"
      :loading="isLoadingTable"
      :page-index="fetchingParams.page - 1"
      :page-size="fetchingParams.page_size"
      :page-sizes="DEFAULT_PAGE_SIZE_OPTIONS"
      :scroller-style="{ height: `calc(100vh - 320px - ${sectionManagementToolbarHeight || 0}px)` }"
      :selectable="launchDarkly.allow_bulk_actions_canvases_files"
      server-side
      :total-count="totalCount"
      @page-changed="onTableChange('pageChanged', $event)"
      @page-size-changed="onTableChange('pageSizeChanged', $event)"
      @sort-changed="onTableChange('sortChanged', $event)"
    />
    <CUpsertFolderModal v-if="showFolderModal" @submit="handleFolderCreate" @update:show="showFolderModal = false" />
    <CBatchMoveItemsModal
      v-if="showMoveModal"
      v-model:show="showMoveModal"
      :current-folder="batchMoveFolder"
      :current-folder-id="currentFolderId"
      :folders="foldersExcludingSelectedFolders"
      :is-fetching-level="isFetchingLevel"
      :is-moving="isMovingItem"
      :path="path"
      :selected-item-ids="movingItemIds"
      @fetch-level="fetchLevel"
      @move-items="handleMoveItems"
    />
    <slot />
  </div>
</template>

<script setup lang="tsx">
import { h, provide, ref, onMounted, computed, inject, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useElementSize, useLocalStorage, useWindowSize, useResizeObserver } from '@vueuse/core'
import { type ColumnDef, createColumnHelper, type PaginationState, type SortingState } from '@tanstack/table-core'
import { NSkeleton } from 'naive-ui'
import CBatchMoveItemsModal from '@lib/components/CBatchMoveItemsModal/CBatchMoveItemsModal.vue'
import CPopularityAndEngagementIcons from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementIcons.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'
import CTable from '@lib/components/CTable/CTable.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CTemplateManagementToolbar from './CTemplateManagementToolbar.vue'
import CTemplateManagementActionsCell from './CTemplateManagementActionsCell.vue'
import CTemplateManagementListItem from './CTemplateManagementListItem.vue'
import CCanvasDeleteDialogContent from '@lib/components/canvases/CCanvasDeleteDialogContent.vue'
import CBulkToolbar from '@lib/components/CBulkToolbar/CBulkToolbar.vue'
import CTooltipWrapper from '@lib/components/CTooltipWrapper/CTooltipWrapper.vue'
import CUpsertFolderModal from '@lib/components/CUpsertFolderModal/CUpsertFolderModal.vue'
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'

import type { PaginatedData } from '@lib/types/paginatedData'
import { CoreFolderEntityType, type CoreFolderContentsRetrieve, type CoreFolderRetrieve } from '@lib/types/folder'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { formatDateTime } from '@lib/util/date'
import useCanvasesManagementBulk from '@lib/components/canvases/CanvasesManagementBulk.use.tsx'
import { CanvasStatus, type CanvasType, type GetCanvasesParams } from '@lib/types/canvases'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import { isModifierClick } from '@lib/util/navigation.util'
import {
  type CanvasRetrieve,
  type CanvasCreateRequest,
  type PatchedCanvasUpdateRequest,
  type PaginatedMetadataTemplateList,
  type User,
  type FolderRetrieve,
} from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useToast from '@lib/composables/useToast'
import useTemplatePopularity from '@canvas-builder/composables/useSectionPopularity'
import { usePopularity, EntityType } from '@canvas-builder/composables/usePopularity'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import { useTableColumnsSettings } from '@lib/composables/tableColumnsSettings.use'
import usePagination from '@lib/composables/useCanvasTablesPagination'
import { isEmpty, isEqual, omit, orderBy, unionBy, debounce } from 'lodash-es'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import useCanvasLocks from '@lib/composables/useCanvasLocks'
import { useCanvasStandardFilters } from '@lib/composables/useCanvasStandardFilters'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const themeVars = useThemeVars()
const confirmation = useConfirmation()
const { toast } = useToast()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const { fetchPopularityData } = usePopularity()
const {
  popularityDict,
  getOrderingConfiguration,
  getSystemFilterSectionIds: getSystemFilterTemplateIds,
} = useTemplatePopularity(computed(() => filters.value))
const { recommendedSectionIds: recommendedIds } = useRecommendations()
const props = defineProps<{
  storageKey?: string
  myUserId?: CanvasRetrieve['created_by']['id']
  updater: (
    params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
  ) => Promise<CanvasRetrieve>
  creator: (params: Omit<CanvasCreateRequest, 'instance_id'>, folderId?: string | null) => Promise<CanvasRetrieve>
  fetcher: (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string; id__in?: string },
  ) => Promise<PaginatedData<CanvasRetrieve>>
  deleter: (id: CanvasRetrieve['id']) => Promise<CanvasRetrieve>
  byIdFetcher: (params: { id: CanvasRetrieve['id']; fields?: string }) => Promise<CanvasRetrieve>
  client: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  canvasType: Exclude<CanvasType, 'section' | 'canvas' | 'block'>
  folderCreator?: (params: { name: string; parent_id?: string | null }) => Promise<any>
  folderDeleter?: (id: string, params: { entity: CoreFolderEntityType }) => Promise<any>
  folderPatcher?: (id: string, params: Partial<CoreFolderRetrieve>) => Promise<any>
  folderItemsFetcher?: (params: { parent_id?: string }) => Promise<FolderRetrieve>
  folderFetcher?: (params: {
    folder_id?: string | null
    entity: CoreFolderEntityType
    tags__not_in?: string
  }) => Promise<any>
  onItemsMove?: (params: {
    target_folder_id: string
    entity: CoreFolderEntityType
    item_ids: string[]
  }) => Promise<void>
  hideConvert?: boolean
  isAnyTypeOfAdmin?: boolean
  excludedTags?: string[]
}>()

const emit = defineEmits<{
  open: [canvas: CanvasRetrieve, newTab?: boolean]
  details: [canvas: CanvasRetrieve]
  edit: [canvas: CanvasRetrieve]
  create: [folderId?: string | null]
  convert: []
  createFromTemplate: [templateId: CanvasRetrieve['id']]
  move: [canvas: CanvasRetrieve]
  manageDistribution: []
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')

const tableRef = ref<any | null>(null)
const listRef = ref<any | null>(null)
const topbarRef = ref<HTMLElement | null>(null)
const toolbarWrapper = ref<any | null>(null)
const myUser = inject<Ref<User | null>>(
  'myUser',
  computed(() => null),
)

const toListItem = (item: any) => item as ListItem

useResizeObserver(
  () => listRef.value?.$el,
  debounce(() => {
    const newOffsetTop = listRef.value?.$el?.offsetTop || 0
    offsetTop.value = newOffsetTop
  }, 100),
)

const containerHeight = computed(() => {
  const effectiveTopbarHeight = topbarHeight.value || 0 // slotty-ui-header's height

  const calculatedHeight = windowHeight.value - (offsetTop.value + effectiveTopbarHeight)

  // Ensure minimum heigh
  return Math.max(calculatedHeight, 300)
})

const activeRecommendedIds = computed(() =>
  filteredAndSortedData.value.filter((item) => recommendedIds.value.includes(item.id)).map((item) => item.id),
)

const foldersExcludingSelectedFolders = computed(() =>
  folders.value.filter((folder) => !movingItemIds.value.includes(folder.id)),
)

const showManageAttributes = computed(
  () =>
    !!(
      (isAdmin.value && !isTemplateFolderEnabled.value && tableRef.value && tableRef.value.selectedRowsCount > 0) ||
      (isTemplateFolderEnabled.value && selectedTemplateIds.value?.length === selectedItemIds.value.length)
    ),
)

const viewModeStorageKey = computed(() => {
  let key = `${props.storageKey}-view-mode` || 'template-management-view-mode'
  if (myUser.value?.id) key = `${key}-user-${myUser.value.id}`
  return key
})

const viewMode = useLocalStorage<'table' | 'list'>(viewModeStorageKey.value, 'list')

const isTemplateFolderEnabled = computed(
  () => !!launchDarkly.value.enable_template_folders && props.canvasType === 'template' && viewMode.value === 'list',
)

const { t } = useI18n()

const {
  bulkDeleteDialog,
  bulkAddTagsDialog,
  bulkRemoveTagsDialog,
  bulkUpdateMetadataDialog,
  bulkUpdateDistributionDialog,
} = useCanvasesManagementBulk()

const breadcrumbs = ref<{ id: string; name: string }[]>([])
const selectedRows = ref<Record<string, boolean>>({})
const isCopyingId = ref<CanvasRetrieve['id']>('')
const deletingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const editingColumn = ref<{ id: CanvasRetrieve['id']; column: string } | null>(null)
const editingItem = ref<{ id: string | number; prop: 'name' | 'tags' } | null>(null)
const fetchedData = ref<(CanvasRetrieve & { type?: string })[]>([])
const isLoading = ref(false)
const isBulkDeleting = ref(false)
const isBulkUpdating = ref(false)
const totalCount = ref(0)
const offsetTop = ref(0)
const isImpact = computed(() => props.client === CLIENT_TYPE.impact)
const isAdmin = computed(() => props.client === CLIENT_TYPE.admin)
const DEFAULT_ORDERING = '-modified_at'

const { height: sectionManagementToolbarHeight } = useElementSize(() => toolbarWrapper.value?.$el)
const { height: topbarHeight } = useElementSize(topbarRef)
const { height: windowHeight } = useWindowSize()

provide('availableTemplates', fetchedData)

const fetchFields = [
  'access_type',
  'id',
  'name',
  'tags',
  'is_draft',
  'modified_at',
  'modified_by',
  'created_by',
  'metadata',
  'is_section',
  'is_block',
  'is_template',
  'folder_id',
  'thumbnail_url',
  ...(props.canvasType === 'section-template' ? [] : ['is_default']),
]

const { filterComponents: dataFilterComponents } = useCanvasStandardFilters({
  includeTemplateFilter: false,
  includeOwnerFilter: true,
})

const initialFilters: Record<string, any> = {}

const {
  search,
  page,
  pageSize,
  selectedFilters: filters,
  ordering,
} = usePagination({
  storageKey: props.storageKey,
  initialFilters,
  initialOrdering: DEFAULT_ORDERING,
})

const fetchingParams = computed(() => ({
  page_size: pageSize.value,
  page: page.value,
  ordering: ordering.value,
  filters: filters.value,
  search: search.value,
  folder_id: isTemplateFolderEnabled.value ? currentFolderId.value : undefined,
}))

const isBulkToolbarDisabled = computed(
  () => !!isCopyingId.value || deletingInProgress.value.size > 0 || isBulkDeleting.value || isBulkUpdating.value,
)

const isLoadingTable = computed(() => isLoading.value || isBulkDeleting.value)

const filteredData = computed(() => {
  if (!search.value) return fetchedData.value

  const currSearch = (search.value ?? '').toLocaleLowerCase()
  return fetchedData.value.filter(
    (item) =>
      item.name.toLowerCase().includes(currSearch) || item.tags?.some((tag) => tag.toLowerCase().includes(currSearch)),
  )
})

const showBulkToolbar = computed(() =>
  isTemplateFolderEnabled.value ? selectedItemIds.value.length > 0 : tableRef.value?.selectedRowsCount,
)

const bulkToolbarCount = computed(() =>
  isTemplateFolderEnabled.value ? totalCount.value : (tableRef.value?.pageRowsCount ?? 0),
)

// Create a storage key for sorting that includes instanceId and userId
const sortStorageKey = computed(() => {
  let key = `${props.storageKey}-sort` || 'template-management-sort'
  if (myUser.value?.id) key = `${key}-user-${myUser.value.id}`
  return key
})

// Store the current sort in local storage, but only for folder-enabled view
const persistedSort = useLocalStorage<{ field: string; order: 'asc' | 'desc' }>(sortStorageKey.value, {
  field: 'name',
  order: 'asc',
})

// Initialize currentSort based on the persisted value or default
const currentSort = ref<{ field: string; order: 'asc' | 'desc' }>(
  isTemplateFolderEnabled.value ? persistedSort.value : { field: 'name', order: 'asc' },
)

// Compute table sorting state from ordering value
const tableSorting = computed<SortingState>(() => {
  const orderingValue = ordering.value || DEFAULT_ORDERING
  const isDescending = orderingValue.startsWith('-')
  const field = isDescending ? orderingValue.substring(1) : orderingValue

  return [{ id: field, desc: isDescending }]
})

function onSortChanged(sort: { field: string; order: 'asc' | 'desc' }) {
  currentSort.value = sort

  // Save to localStorage if in folder-enabled mode
  if (isTemplateFolderEnabled.value) {
    persistedSort.value = sort
  }

  onTableChange('sortChanged', [{ id: sort.field, desc: sort.order === 'desc' }])
}

const filteredAndSortedData = computed(() => {
  if (!isTemplateFolderEnabled.value) return filteredData.value

  // When using folder view, separate folders and templates
  if (isTemplateFolderEnabled.value) {
    const folders = filteredData.value.filter((item) => isFolder(toListItem(item)))
    const templates = filteredData.value.filter((item) => !isFolder(toListItem(item)))

    // Sort folders using the same criteria as templates
    const { field, order } = currentSort.value

    // Use case-insensitive sorting for name field
    if (field === 'name') {
      // Create a case-insensitive sorter function for name
      const nameSorter = (item: any) => item.name?.toLowerCase?.() || ''
      const sortedFolders = orderBy(folders, [nameSorter], [order])
      const sortedTemplates = orderBy(templates, [nameSorter], [order])

      // Combine: folders first, then templates
      return [...sortedFolders, ...sortedTemplates]
    } else {
      // For other fields, use normal sorting
      const sortedFolders = orderBy(folders, [field], [order])
      const sortedTemplates = orderBy(templates, [field], [order])

      // Combine: folders first, then templates
      return [...sortedFolders, ...sortedTemplates]
    }
  }

  const sorters = getOrderingConfiguration()
  return orderBy(
    filteredData.value,
    sorters.map(([sorter]) => sorter),
    sorters.map(([, order]) => order),
  )
})

const showFolderModal = ref(false)
const isCreatingFolder = ref(false)
const showMoveModal = ref(false)
const movingItemIds = ref<string[]>([])
const isMovingItem = ref(false)
const isFetchingLevel = ref(false)
const isFetchingData = ref(false)
const folders = ref<any[]>([])
const folderStorageKey = computed(() => `${props.storageKey || 'default'}-folder-id`)
const currentFolderId = useLocalStorage<CoreFolderContentsRetrieve['id'] | null>(folderStorageKey.value, null)
const batchMoveFolder = ref<{ id: string | null; name: string }>({ id: null, name: '' })
const path = ref<{ id: string | null; name: string }[]>([])

const selectedItemIds = computed(() => Object.keys(selectedRows.value).filter((id) => selectedRows.value[id]))
const selectedTemplateIds = computed(
  () =>
    selectedItemIds.value.filter((id) => fetchedData.value.find((item) => item.id === id && item.is_template)) ?? [],
)
const selectedFolderIds = computed(() =>
  selectedItemIds.value.filter((id) => fetchedData.value.find((item) => item.id === id && isFolder(toListItem(item)))),
)

interface ListItem extends Partial<CanvasRetrieve> {
  id: string
  name: string
  type?: 'folder'
  is_draft?: boolean
  is_default?: boolean
  tags?: string[]
  permissions?: { can_share: boolean }
  content_type?: string
  folder?: { name: string }
}

const {
  lockCanvas,
  unlockCanvas,
  isCanvasLocked,
  getCanvasLock,
  isLockedByCurrentUser,
  locks,
  getCanvasLockTooltip,
  isOrgAdmin,
} = useCanvasLocks()

function syncSortingForTableView() {
  const isDescending = ordering.value.startsWith('-')
  const field = isDescending ? ordering.value.substring(1) : ordering.value
  const tableSortableFields = ['name', 'modified_at', 'is_draft', 'owned_by__name']
  const tableSortField = tableSortableFields.includes(field) ? field : 'modified_at'
  const tableSortDesc = field === tableSortField ? isDescending : true

  if (tableSortField !== field) {
    ordering.value = tableSortDesc ? `-${tableSortField}` : tableSortField
  }
}

function syncSortingForListView() {
  const isDescending = ordering.value.startsWith('-')
  const field = isDescending ? ordering.value.substring(1) : ordering.value
  const listCompatibleFields = ['name', 'modified_at']
  const listSortField = listCompatibleFields.includes(field) ? field : 'modified_at'
  const listSortOrder = listSortField === field ? (isDescending ? 'desc' : 'asc') : 'desc'

  currentSort.value = { field: listSortField, order: listSortOrder }
  persistedSort.value = currentSort.value

  if (listSortField !== field) {
    ordering.value = listSortOrder === 'desc' ? `-${listSortField}` : listSortField
  }
}

function toggleViewMode() {
  const previousViewMode = viewMode.value
  viewMode.value = viewMode.value === 'table' ? 'list' : 'table'

  if (viewMode.value === 'table') {
    selectedRows.value = {}
    syncSortingForTableView()
  } else if (viewMode.value === 'list') {
    syncSortingForListView()
  }

  // Re-run initialization logic similar to onMounted
  isLoading.value = true

  // Re-fetch data based on the new view mode
  if (viewMode.value === 'list' && previousViewMode === 'table' && isTemplateFolderEnabled.value) {
    // When switching to list view, initialize folder view
    fetchByParentId(currentFolderId.value).finally(() => {
      isLoading.value = false
    })
  } else {
    // When switching to table view or re-initializing, fetch using standard parameters
    fetch(fetchingParams.value).finally(() => {
      isLoading.value = false
    })
  }

  // Re-fetch popularity data if needed
  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.TEMPLATE)
  }
}

async function handleFolderCreate(name: string) {
  try {
    isCreatingFolder.value = true
    await props.folderCreator?.({
      name,
      parent_id: currentFolderId.value,
    })
    showFolderModal.value = false
    toast.success(
      t('canvasUI.components.templates.CTemplateManagement.notifications.folderCreate.success', {
        name,
      }),
    )
    onTableChange('itemsChanged')
    fetchByParentId(currentFolderId.value)
  } catch (e) {
    console.error('folder creation failed:', e)
    toast.error(
      t('canvasUI.components.templates.CTemplateManagement.notifications.folderCreate.error', {
        name,
      }),
    )
  } finally {
    isCreatingFolder.value = false
    showFolderModal.value = false
  }
}

function updateFetchedData(updatedItem: CanvasRetrieve) {
  const foundIndex = fetchedData.value.findIndex((template) => template.id === updatedItem.id)
  if (foundIndex !== -1) {
    fetchedData.value = [
      ...fetchedData.value.slice(0, foundIndex),
      updatedItem,
      ...fetchedData.value.slice(foundIndex + 1),
    ]
  }
}

async function updateProperty<T extends keyof CanvasRetrieve>(
  item: CanvasRetrieve,
  property: T,
  newValue: CanvasRetrieve[T],
) {
  try {
    const updatedItem = await props.updater({
      id: item.id,
      [property]: newValue,
      fields: fetchFields.join(','),
    })

    updateFetchedData(updatedItem)
  } catch (e) {
    console.error('template update failed:', e)
  }
}

async function onToggleDefault(item: CanvasRetrieve) {
  const removeDefaultTemplate = () => {
    confirmation.danger({
      hasExit: false,
      title: t('canvasUI.components.templates.CTemplateManagement.removeDefaultTemplateDialog.title'),
      content: t('canvasUI.components.templates.CTemplateManagement.removeDefaultTemplateDialog.content'),
      confirmText: t('canvasUI.common.remove'),
      cancelText: t('canvasUI.common.cancel'),
      onConfirm: async () => {
        try {
          await updateProperty(item, 'is_default', false)

          toast.success(
            t('canvasUI.components.templates.CTemplateManagement.notifications.removeDefaultTemplate.success', {
              name: item.name,
            }),
          )
        } catch (e) {
          console.error('remove default template failed:', e)

          toast.error(
            t('canvasUI.components.templates.CTemplateManagement.notifications.removeDefaultTemplate.error', {
              name: item.name,
            }),
          )
        }
      },
    })
  }

  const setDefaultTemplate = async () => {
    try {
      await updateProperty(item, 'is_default', true)

      const formerDefaultTemplateIndex = fetchedData.value.findIndex(
        (template) => template.is_default && template.id !== item.id,
      )
      if (formerDefaultTemplateIndex !== -1) {
        fetchedData.value[formerDefaultTemplateIndex].is_default = false
      }

      toast.success(
        t('canvasUI.components.templates.CTemplateManagement.notifications.setDefaultTemplate.success', {
          name: item.name,
        }),
      )
    } catch (e) {
      console.error('set default template failed:', e)

      toast.error(
        t('canvasUI.components.templates.CTemplateManagement.notifications.setDefaultTemplate.error', {
          name: item.name,
        }),
      )
    }
  }

  item.is_default ? removeDefaultTemplate() : setDefaultTemplate()
}

async function onUpdateStatus(item: CanvasRetrieve, newValue: CanvasStatus) {
  if (props.canvasType === 'template' && item.is_default && newValue === CanvasStatus.DRAFT) {
    confirmation.warning({
      hasExit: false,
      hasCancel: false,
      title: t('canvasUI.components.templates.CTemplateManagement.setDefaultTemplateToDraftDialog.title'),
      content:
        props.client === 'admin'
          ? t('canvasUI.components.templates.CTemplateManagement.setDefaultTemplateToDraftDialog.adminContent', {
              name: item.name,
            })
          : t('canvasUI.components.templates.CTemplateManagement.setDefaultTemplateToDraftDialog.impactContent', {
              name: item.name,
            }),
      confirmText: t('canvasUI.common.close'),
    })

    return
  }

  return await updateProperty(item, 'is_draft', newValue === CanvasStatus.DRAFT)
}

async function deleteItem(item: CanvasRetrieve) {
  if (isFolder(item)) return deleteFolder(item)
  return deleteCanvas(item)
}

async function deleteCanvas(item: CanvasRetrieve) {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.title'),
    content: () =>
      h(CCanvasDeleteDialogContent, {
        name: item?.name,
        texts: {
          name: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.content.name'),
          info: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.content.info'),
        },
      }),
    confirmText: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.confirm'),
    cancelText: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.cancel'),
    onConfirm: async () => {
      try {
        deletingInProgress.value.add(item.id)
        await props.deleter(item.id)
        fetchedData.value = fetchedData.value.filter((canvas) => canvas.id !== item.id)
        totalCount.value = Math.max(0, totalCount.value - 1)

        toast.success(
          t('canvasUI.components.templates.CTemplateManagement.notifications.delete.success', {
            name: item.name,
          }),
        )
      } catch (e) {
        console.error('template deletion failed:', e)

        toast.error(
          t('canvasUI.components.templates.CTemplateManagement.notifications.delete.error', {
            name: item.name,
          }),
        )
      } finally {
        selectedRows.value = {}
        deletingInProgress.value.delete(item.id)
      }
    },
  })
}

async function deleteFolder(item: CanvasRetrieve) {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.title'),
    content: () =>
      h(CCanvasDeleteDialogContent, {
        name: item?.name,
        texts: {
          name: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.content.name'),
          info: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.content.info'),
        },
      }),
    confirmText: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.confirm'),
    cancelText: t('canvasUI.components.templates.CTemplateManagement.deleteDialog.cancel'),
    onConfirm: async () => {
      try {
        deletingInProgress.value.add(item.id)
        await props.folderDeleter?.(item.id, { entity: CoreFolderEntityType.Template })
        fetchedData.value = fetchedData.value.filter((canvas) => canvas.id !== item.id)
        totalCount.value = Math.max(0, totalCount.value - 1)

        toast.success(
          t('canvasUI.components.templates.CTemplateManagement.notifications.delete.success', {
            name: item.name,
          }),
        )
      } catch (e) {
        console.error('template deletion failed:', e)

        toast.error(
          t('canvasUI.components.templates.CTemplateManagement.notifications.delete.error', {
            name: item.name,
          }),
        )
      } finally {
        selectedRows.value = {}
        deletingInProgress.value.delete(item.id)
      }
    },
  })
}

async function bulkDelete() {
  bulkDeleteDialog({
    selectedRows,
    filteredData,
    isBulkDeleting,
    canvasType: props.canvasType,
    isReadonly: isReadonlyBulk,
    onComplete: () => {
      isTemplateFolderEnabled.value && viewMode.value === 'list'
        ? fetchByParentId(currentFolderId.value)
        : onTableChange('itemsChanged')
    },
  })
}

async function bulkAddTags() {
  bulkAddTagsDialog({
    selectedRows,
    filteredData,
    isBulkUpdating,
    canvasType: props.canvasType,
    isReadonly: isReadonlyBulk,
    onComplete: () => {
      isTemplateFolderEnabled.value && viewMode.value === 'list'
        ? fetchByParentId(currentFolderId.value)
        : onTableChange('itemsChanged')
    },
  })
}

async function bulkRemoveTags() {
  bulkRemoveTagsDialog({
    selectedRows,
    filteredData,
    isBulkUpdating,
    canvasType: props.canvasType,
    isReadonly: isReadonlyBulk,
    onComplete: () => {
      isTemplateFolderEnabled.value && viewMode.value === 'list'
        ? fetchByParentId(currentFolderId.value)
        : onTableChange('itemsChanged')
    },
  })
}

async function bulkUpdateMetadata() {
  bulkUpdateMetadataDialog({
    selectedRows,
    selectedItemNames: filteredData.value?.filter((item) => selectedRows.value[item.id]).map((item) => item.name),
    metadataFetcher,
    updater: props.updater,
    onComplete: () => {
      isTemplateFolderEnabled.value && viewMode.value === 'list'
        ? fetchByParentId(currentFolderId.value)
        : onTableChange('itemsChanged')
    },
    isBulkUpdating,
    canvasType: 'template',
  })
}

function isFolder(item: ListItem): boolean {
  return item?.type === 'folder'
}

function isLockedBySomeoneElse(item: ListItem): boolean {
  return !!getCanvasLock(item.id) && !isLockedByCurrentUser(item.id)
}

function isReadonly(item: ListItem): boolean {
  return (
    (isImpact.value && item.created_by?.id !== props.myUserId) ||
    !!editingColumn.value?.id ||
    isLockedBySomeoneElse(item)
  )
}

function isReadonlyBulk(item: CanvasRetrieve) {
  return isImpact.value && item.created_by.id !== props.myUserId
}

function hasHomeOrShareTag(tags?: string[]): boolean {
  return (
    tags?.some((tag: string) => {
      const lowerTag = tag.toLowerCase()
      return lowerTag === 'home' || lowerTag === 'share'
    }) ?? false
  )
}

function hasSystemOrExcludedTag(tags?: string[]): boolean {
  const systemTags = ['home', 'share', 'sharebox']
  return (
    tags?.some((tag: string) => {
      const lowerTag = tag.toLowerCase()
      return systemTags.includes(lowerTag) || (props.excludedTags?.map((t) => t.toLowerCase()) ?? []).includes(lowerTag)
    }) ?? false
  )
}

function isEditingCurrentItem(id: string | number, prop: 'name' | 'tags') {
  return editingItem.value?.id === id && editingItem.value?.prop === prop
}

function clearInlineEditingItem() {
  editingItem.value = null
}

function onEdit(id: string | number, prop: 'name' | 'tags') {
  editingItem.value = { id, prop }
}

const columnHelper = createColumnHelper<CanvasRetrieve>()

const defaultColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    cell: (cellCtx) =>
      h(
        CTableInput,
        {
          modelValue: cellCtx.getValue(),
          state: isEditingCurrentItem(cellCtx.row.original.id, 'name') ? 'edit' : 'view',
          readonly: isReadonly(cellCtx.row.original),
          class: 'flex items-center cursor-pointer min-h-[36px]',
          valueClass: 'font-700 underline unerline-solid',
          'onUpdate:modelValue': (newValue) => {
            updateProperty(cellCtx.row.original, 'name', newValue)
            clearInlineEditingItem()
          },
          onClick: async (e: MouseEvent) => {
            emit('open', cellCtx.row.original, isModifierClick(e))
          },
          onEdit: () => {
            onEdit(cellCtx.row.original.id, 'name')
          },
          onCancel: clearInlineEditingItem,
        },
        {
          prefix: () => {
            return (
              <div class="flex items-center">
                {isTemplateFolderEnabled.value && (
                  <CIcon
                    icon={!isFolder(cellCtx.row.original) ? 'presentation-screen' : 'folder'}
                    color={themeVars.value.primary4}
                    faType="fas"
                    size="20px"
                    class="mr-2"
                  />
                )}
                {pitcherSettings.value.enable_thumbnails_for_templates_and_blocks && (
                  <CThumbnail
                    class="mr-2"
                    thumbnailUrl={
                      (cellCtx.row.original as any).thumbnail_url ?? cellCtx.row.original.metadata?.thumbnail
                    }
                    item={cellCtx.row.original}
                    themeVars={themeVars.value}
                    enableThumbnails={true}
                    isFolder={isFolder(cellCtx.row.original)}
                  />
                )}
                {!!launchDarkly.value.enable_canvas_locks && isCanvasLocked(cellCtx.row.original.id) && (
                  <CIcon
                    v-tooltip={getCanvasLockTooltip(String(cellCtx.row.original.id))}
                    icon="lock"
                    color={themeVars.value.primary4}
                    faType="fas"
                    size="20px"
                    class="mr-2"
                  />
                )}
              </div>
            )
          },
          suffix: () =>
            h(
              'div',
              {
                class: 'flex items-center gap-2 ml-4',
              },
              [
                cellCtx.row.original?.access_type === 'restricted' &&
                  h(CContentError, {
                    onlyIcon: true,
                    restricted: true,
                  }),
                h(CPopularityAndEngagementIcons, {
                  class: 'flex gap-2',
                  popularity: launchDarkly.value.show_popularity_icons
                    ? popularityDict.value[cellCtx.row.original.id]?.popularity
                    : undefined,
                  isRecommended: recommendedIds.value.includes(cellCtx.row.original.id),
                  engagement: launchDarkly.value.show_popularity_icons
                    ? popularityDict.value[cellCtx.row.original.id]?.engagement
                    : undefined,
                  views: launchDarkly.value.show_popularity_icons
                    ? (popularityDict.value[cellCtx.row.original.id]?.views ?? 0)
                    : 0,
                }),
                props.canvasType === 'template' &&
                  props.client === 'admin' &&
                  cellCtx.row.original.is_default &&
                  h(
                    CTooltipWrapper,
                    {
                      tooltip: t(
                        'canvasUI.components.templates.CTemplateManagement.actions.buttons.tooltips.defaultTemplate',
                      ),
                      class: 'flex items-center align-center w-6 h-5',
                    },
                    () => [
                      h(CIcon, {
                        icon: 'solid-presentation-screen-circle-check',
                        faType: 'fa-kit',
                        size: 'large',
                        color: themeVars.value.success,
                        class: 'cursor-default w-5 h-5',
                        onClick: (e: MouseEvent) => e.stopPropagation(),
                      }),
                    ],
                  ),
              ],
            ),
        },
      ),
    header: t('canvasUI.components.templates.CTemplateManagement.nameTh'),
  }),
  columnHelper.accessor((row) => row.tags, {
    id: 'tags',
    cell: (cellCtx) =>
      h(CTableTag, {
        modelValue: cellCtx.getValue() ?? [],
        state: isEditingCurrentItem(cellCtx.row.original.id, cellCtx.column.id as 'name' | 'tags') ? 'edit' : 'view',
        readonly: isReadonly(cellCtx.row.original),
        rowIndex: cellCtx.row.index,
        suggestedTagsFetcher:
          props.canvasType === 'template'
            ? (search) => fetchSuggestedInstanceTags(search, { type: ['canvases'], canvasType: 'template' })
            : undefined,
        'onUpdate:modelValue': (newValue) => {
          updateProperty(cellCtx.row.original, 'tags', newValue)
          clearInlineEditingItem()
        },
        onEdit: () => {
          onEdit(cellCtx.row.original.id, 'tags')
        },
        onCancel: clearInlineEditingItem,
      }),
    header: t('canvasUI.components.templates.CTemplateManagement.tagsTh'),
    enableSorting: false,
    meta: {
      classes: launchDarkly.value.enable_short_tag_column_in_tables ? '!max-w-[200px]' : undefined,
    },
  }),
  columnHelper.accessor((row) => row.is_draft, {
    id: 'is_draft',
    cell: (cellCtx) => (
      <div class="flex items-center">
        <CSwitch
          checked={!cellCtx.getValue()}
          disabled={isReadonly(cellCtx.row.original)}
          onChange={async (newValue: boolean) =>
            await onUpdateStatus(cellCtx.row.original, newValue ? CanvasStatus.READY : CanvasStatus.DRAFT)
          }
          themeOverrides={{
            railColorActive: themeVars.value.success,
            railColor: themeVars.value.primary6,
          }}
        />
      </div>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagementStatusCell.status.ready'),
    meta: {
      classes: 'w-[110px]',
    },
  }),
  columnHelper.accessor((row) => row.created_by, {
    // change to created_by__name when fixed
    id: 'owned_by__name',
    cell: (cellCtx) => {
      const createdBy = cellCtx.row.original.created_by.name || cellCtx.row.original.created_by.email || ''
      return h('div', { class: 'min-w-[150px]' }, createdBy)
    },
    header: t('canvasUI.components.templates.CTemplateManagement.createdByTh'),
  }),
  columnHelper.accessor((row) => row.modified_at, {
    id: 'modified_at',
    cell: (cellCtx) => {
      const modifiedBy = cellCtx.row.original.modified_by.name || cellCtx.row.original.modified_by.email
      const modifiedAt = formatDateTime(cellCtx.row.original.modified_at) as string
      const textColor = modifiedBy ? themeVars.value.text : themeVars.value.text3

      return h('div', { class: 'min-w-[250px]' }, [
        h('span', { style: { color: textColor } }, modifiedBy),
        ' ',
        h('span', {}, t('canvasUI.components.templates.CTemplateManagement.atTh')),
        ' ',
        h('span', {}, modifiedAt),
      ])
    },
    header: t('canvasUI.components.templates.CTemplateManagement.modifiedByTh'),
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    cell: (cellCtx) =>
      !isTemplateFolderEnabled.value || !isFolder(cellCtx.row.original)
        ? h(CTemplateManagementActionsCell, {
            canvasId: cellCtx.row.original.id,
            hideCopy: isImpact.value,
            hideViewDetails: isImpact.value,
            hideMove: !isTemplateFolderEnabled.value,
            hideEdit: isReadonly(cellCtx.row.original),
            hideDelete: isReadonly(cellCtx.row.original),
            hideLock:
              !launchDarkly.value.enable_canvas_locks ||
              (isOrgAdmin.value ? isImpact.value : isLockedBySomeoneElse(cellCtx.row.original) || isImpact.value),
            hideCreateFromTemplate:
              isAdmin.value || !!cellCtx.row.original.is_draft || hasSystemOrExcludedTag(cellCtx.row.original.tags),
            hideToggleDefault:
              props.canvasType === 'section-template' ||
              !!cellCtx.row.original.is_draft ||
              isImpact.value ||
              (hasHomeOrShareTag(cellCtx.row.original.tags) && !cellCtx.row.original.is_default),
            hideToggleStatus: isReadonly(cellCtx.row.original) || isFolder(cellCtx.row.original),
            hideViewNewTab: isPitcherWkWebView,
            isCopying: isCopyingId.value === cellCtx.row.original.id,
            isDeleting: deletingInProgress.value.has(cellCtx.cell.row.original.id),
            isDefault: cellCtx.row.original.is_default,
            isDraft: cellCtx.row.original.is_draft,
            onCopy: () => onCopy(cellCtx.row.original),
            onMove: () => onMove(cellCtx.row.original),
            onCreateFromTemplate: () => emit('createFromTemplate', cellCtx.row.original.id.toString()),
            onEdit: () => emit('edit', cellCtx.row.original),
            onDelete: () => deleteItem(cellCtx.row.original),
            onView: () => emit('open', cellCtx.row.original),
            onViewNewTab: () => emit('open', cellCtx.cell.row.original, true),
            onViewDetails: () => emit('details', cellCtx.row.original),
            onToggleDefault: async () => onToggleDefault(cellCtx.row.original),
            onToggleStatus: () =>
              onUpdateStatus(
                cellCtx.row.original,
                cellCtx.row.original.is_draft ? CanvasStatus.READY : CanvasStatus.DRAFT,
              ),
            onLock: () => lockCanvas(cellCtx.row.original.id),
            onUnlock: () => unlockCanvas(getCanvasLock(cellCtx.row.original.id)?.id),
          })
        : null,
    header: t('canvasUI.components.templates.CTemplateManagement.actionsTh'),
    enableSorting: false,
  }),
] as ColumnDef<Record<string, any>, any>[]

const TABLE_COLUMNS_SETTINGS_KEY =
  props.canvasType === 'section-template'
    ? 'ciq_section_templates_management_table_columns_settings'
    : 'ciq_templates_management_table_columns_settings'

const { tableColumnsSettings, columnsFromSettings, onColumnsMove, onColumnsSelect, restoreColumns } =
  useTableColumnsSettings(TABLE_COLUMNS_SETTINGS_KEY, defaultColumns)

function onTableChange(
  event: 'pageChanged' | 'pageSizeChanged' | 'sortChanged' | 'searchChanged' | 'itemsChanged' | 'filterValuesChanged',
  state?: number | SortingState | PaginationState | string | Record<string, any>,
) {
  const initialParams = { ...fetchingParams.value }
  let change
  switch (event) {
    case 'pageChanged':
      change = state as PaginationState
      page.value = change.pageIndex + 1
      break
    case 'pageSizeChanged':
      change = state as number
      pageSize.value = change ?? fetchingParams.value.page_size
      page.value = 1
      break
    case 'sortChanged':
      ;[change] = state as SortingState
      ordering.value = change?.desc ? '-' + change.id : (change?.id ?? DEFAULT_ORDERING)

      // Update currentSort when ordering changes (only for folder-enabled view)
      if (isTemplateFolderEnabled.value && change) {
        const field = change.id
        const order = change.desc ? 'desc' : 'asc'
        currentSort.value = { field, order }
        persistedSort.value = currentSort.value
      }

      page.value = 1
      break
    case 'searchChanged':
      change = state as string
      search.value = change
      page.value = 1
      break
    case 'itemsChanged':
      page.value = 1
      break
    case 'filterValuesChanged':
      change = state as Record<string, any>
      filters.value = change
      page.value = 1
      break
  }

  clearInlineEditingItem()

  if (event === 'itemsChanged' || !isEqual(initialParams, fetchingParams.value)) {
    selectedRows.value = {}

    // Only fetch in these cases:
    // 1. When parameters change (regardless of folder mode)
    // 2. When itemsChanged is triggered but NOT in folder mode
    if (!isEqual(initialParams, fetchingParams.value) || (event === 'itemsChanged' && !isTemplateFolderEnabled.value)) {
      fetch(fetchingParams.value)
    }
  }
}

async function fetch(
  params: {
    page_size: number
    page: number
    ordering: string
    search: string
    filters: Record<string, any>
    folder_id?: string | null
  },
  force = false,
) {
  try {
    isLoading.value = true
    if (isTemplateFolderEnabled.value && !search.value && isEmpty(filters.value)) {
      selectedRows.value = {}
      isFetchingData.value = true

      const response: CoreFolderContentsRetrieve = await props.folderFetcher?.({
        folder_id: params.folder_id,
        entity: CoreFolderEntityType.Template,
        tags__not_in: props.excludedTags?.length ? props.excludedTags.join(',') : undefined,
      })

      fetchedData.value = [
        ...(response.folders ?? []).map((folder: any) => ({ type: 'folder', ...folder })),
        ...(response.contents?.filter((item) => (!props.isAnyTypeOfAdmin ? !item.is_draft : true)) ?? []),
      ]

      totalCount.value = (response.folders?.length ?? 0) + (response.contents?.length ?? 0)

      if (!params.folder_id) {
        breadcrumbs.value = [{ id: '', name: t('canvasUI.components.templates.CTemplateManagement.allTemplates') }]
      } else {
        // Start with the root breadcrumb
        const newBreadcrumbs = [{ id: '', name: t('canvasUI.components.templates.CTemplateManagement.allTemplates') }]

        // Add path items if available
        if (response.path?.length) {
          const validPathItems = response.path.filter((item: { id: string; name: string }) => item.id && item.name)
          newBreadcrumbs.push(...validPathItems)
        }

        // Make sure the current folder is included in the breadcrumbs
        if (response.id && response.name) {
          const currentFolderInPath = newBreadcrumbs.some((item) => item.id === response.id)
          if (!currentFolderInPath) {
            newBreadcrumbs.push({ id: response.id, name: response.name })
          }
        }

        breadcrumbs.value = newBreadcrumbs
      }

      isFetchingData.value = false
    } else {
      const requestParams = params.search ? params : omit(params, 'search')

      const idsToInclude = getSystemFilterTemplateIds()

      // only check for null value not empty array
      if (!idsToInclude && !force) {
        totalCount.value = 0
        isLoading.value = false
        return
      }

      const dataFilters: Record<string, any> = {}
      const metaDataFilters: Record<string, any> = {}

      Object.entries(requestParams.filters).forEach(([key, value]) => {
        if (dataFilterComponents.value.some((component) => component.name === key)) {
          dataFilters[key] = value
        } else {
          metaDataFilters[key] = value
        }
      })

      const normalizedMetaDataFilters = normalizeFilterParams(metaDataFilters)

      const requestPromises = [
        props.fetcher({
          ...requestParams,
          page_size: isTemplateFolderEnabled.value ? 500 : requestParams.page_size,
          ordering: isTemplateFolderEnabled.value ? 'name' : requestParams.ordering,
          filters: normalizedMetaDataFilters,
          ...dataFilters,
          is_template: props.canvasType === 'template' || props.canvasType === 'section-template',
          is_section: props.canvasType === 'section-template',
          fields: fetchFields.join(','),
          id__in: idsToInclude?.length ? idsToInclude.slice(0, requestParams.page_size).join(',') : undefined,
          tags__not_in: props.excludedTags?.length ? props.excludedTags.join(',') : undefined,
        }),
      ]

      // only show all recommended sections if on the first page
      if (recommendedIds.value?.length && fetchingParams.value.page === 1 && !idsToInclude?.length)
        requestPromises.push(
          props.fetcher({
            ...requestParams,
            filters: normalizedMetaDataFilters,
            ...dataFilters,
            is_template: props.canvasType === 'template' || props.canvasType === 'section-template',
            is_section: props.canvasType === 'section-template',
            fields: fetchFields.join(','),
            id__in: recommendedIds.value.join(','),
            tags__not_in: props.excludedTags?.length ? props.excludedTags.join(',') : undefined,
          }),
        )

      const [response, recommendedResponse] = await Promise.all(requestPromises)

      if (recommendedResponse && recommendedResponse.results?.length) {
        totalCount.value = response.count

        const withRecommended = unionBy(response.results, recommendedResponse.results, 'id')

        fetchedData.value = withRecommended
      } else {
        totalCount.value = response.count
        fetchedData.value = response.results
      }
    }
  } catch (e) {
    console.error(e)
    if ((e as { status?: number } | undefined)?.status === 404) {
      currentFolderId.value = null
      return await fetch(fetchingParams.value)
    }
  } finally {
    isLoading.value = false
  }
}

async function fetchByParentId(parentId: string | null) {
  if (!isTemplateFolderEnabled.value) return

  currentFolderId.value = parentId
  await fetch(fetchingParams.value)
}

async function fetchLevel(folderId?: string | null) {
  try {
    isFetchingLevel.value = true

    const response = await props.folderFetcher?.({
      folder_id: folderId,
      entity: CoreFolderEntityType.Template,
      tags__not_in: props.excludedTags?.length ? props.excludedTags.join(',') : undefined,
    })

    if (response?.id) {
      // TODO: filter out selected items once bulk actions are implemented
      folders.value = response.folders
      batchMoveFolder.value = { id: response.id, name: response.name }
      path.value = response?.path?.length ? response.path : [{ id: null, name: '' }]
    } else {
      // Reset to root
      batchMoveFolder.value = { id: null, name: '' }
      path.value = []
    }

    folders.value = response.folders
  } catch (e) {
    console.error(e)
    folders.value = []
    batchMoveFolder.value = { id: null, name: '' }
    path.value = []
  } finally {
    isFetchingLevel.value = false
  }
}

async function handleMoveItems(
  targetFolderId: string | null,
  itemIds: string[],
  { isDropAction = false }: { isDropAction?: boolean; isDrag?: boolean } = {},
) {
  if (isDropAction && !isFolder(toListItem(fetchedData.value.find((f) => f.id === targetFolderId)))) {
    toast.error(t('canvasUI.components.templates.CTemplateManagement.notifications.move.cannotMoveToATemplate'))
    return
  }

  if (itemIds.includes(targetFolderId ?? '')) {
    toast.error(t('canvasUI.components.templates.CTemplateManagement.notifications.move.cannotMoveToSelf'))
    return
  }

  try {
    isLoading.value = true
    isMovingItem.value = true
    await props.onItemsMove?.({
      target_folder_id: targetFolderId ?? '',
      entity: CoreFolderEntityType.Template,
      item_ids: itemIds,
    })
    showMoveModal.value = false

    // Show success notification
    toast.success(
      t('canvasUI.components.templates.CTemplateManagement.notifications.move.success', {
        count: itemIds.length,
      }),
    )

    // Navigate to the target folder after moving items
    if (targetFolderId !== currentFolderId.value) {
      if (search.value) {
        search.value = ''
        // Reset search in toolbar component
        if (toolbarWrapper.value) {
          onTableChange('searchChanged', '')
        }
      }
      await fetchByParentId(targetFolderId)
    } else {
      onTableChange('itemsChanged')
    }
  } catch (e) {
    console.error(e)
    toast.error(t('canvasUI.components.templates.CTemplateManagement.notifications.move.error'))
  } finally {
    isMovingItem.value = false
    isLoading.value = false
  }
}

function onMove(item?: CanvasRetrieve, { isBulk }: { isBulk?: boolean } = {}) {
  if (item) movingItemIds.value = [item.id]
  else if (isBulk) movingItemIds.value = selectedItemIds.value
  else return

  // Set the initial folder to the current folder
  batchMoveFolder.value = {
    id: currentFolderId.value,
    name: currentFolderId.value
      ? fetchedData.value.find((f) => f.id === currentFolderId.value && isFolder(toListItem(f)))?.name || ''
      : '',
  }

  showMoveModal.value = true
}

async function onUpdateName(id: string, value: string) {
  const item = fetchedData.value.find((item) => item.id === id)
  if (item) {
    if (isFolder(toListItem(item))) {
      await props
        .folderPatcher?.(item.id, { name: value })
        .then((updatedItem) => updateFetchedData({ ...updatedItem, type: 'folder' }))
    } else {
      updateProperty(item, 'name', value)
    }
    clearInlineEditingItem()
  }
}

function onUpdateTags(id: string, value: string[]) {
  const item = fetchedData.value.find((item) => item.id === id)
  if (item) {
    updateProperty(item, 'tags', value)
    clearInlineEditingItem()
  }
}

async function onCopy(item: CanvasRetrieve) {
  try {
    isCopyingId.value = item.id
    let copyIdx = 1
    let copyName = `${item.name} - ${t('canvasUI.components.templates.CTemplateManagement.copy')}_${copyIdx}`
    while (fetchedData.value.some((canvas) => canvas.name === copyName)) {
      copyIdx++
      copyName = `${item.name} - ${t('canvasUI.components.templates.CTemplateManagement.copy')}_${copyIdx}`
    }
    const { is_default, id, owned_by, created_by, ...toCopyFrom } = await props.byIdFetcher({
      id: item.id,
    })
    const copy = await props.creator(
      {
        ...toCopyFrom,
        name: copyName,
        tags: toCopyFrom.tags ?? [],
        is_template: true,
        is_section: props.canvasType === 'section-template',
      },
      isTemplateFolderEnabled.value ? currentFolderId.value : undefined,
    )
    fetchedData.value = [copy, ...fetchedData.value]
    totalCount.value += 1

    toast.success(
      t('canvasUI.components.templates.CTemplateManagement.notifications.duplicate.success', {
        name: item.name,
      }),
    )
  } catch (e) {
    console.error('template copy failed:', e)

    toast.error(
      t('canvasUI.components.templates.CTemplateManagement.notifications.duplicate.error', {
        name: item.name,
      }),
    )
  } finally {
    selectedRows.value = {}
    isCopyingId.value = ''
  }
}

function toggleItemSelection(item: any) {
  // Prevent selecting items that are locked by someone else
  if (
    !!launchDarkly.value.enable_canvas_locks &&
    !isFolder(toListItem(item)) &&
    isCanvasLocked(item.id.toString()) &&
    !isLockedByCurrentUser(item.id.toString())
  ) {
    return
  }
  selectedRows.value[item.id] = !selectedRows.value[item.id]
}

function toggleSelectAll() {
  if (isTemplateFolderEnabled.value) {
    // Get the count of selectable items (excluding locked items)
    const selectableItems = fetchedData.value.filter(
      (item) =>
        isFolder(toListItem(item)) || !isCanvasLocked(item.id.toString()) || isLockedByCurrentUser(item.id.toString()),
    )

    // Check if all selectable items are already selected
    const allSelectableSelected = selectableItems.every((item) => selectedRows.value[item.id])

    if (allSelectableSelected) {
      // If all selectable items are selected, deselect all
      selectedRows.value = {}
    } else {
      // Otherwise, select all selectable items
      selectedRows.value = fetchedData.value.reduce(
        (acc, item) => {
          // Skip items that are locked by someone else
          if (
            !isFolder(toListItem(item)) &&
            isCanvasLocked(item.id.toString()) &&
            !isLockedByCurrentUser(item.id.toString())
          ) {
            return acc
          }
          acc[item.id] = true
          return acc
        },
        {} as Record<string, boolean>,
      )
    }
  } else {
    tableRef.value?.selectAll()
  }
}

function onOpenFile(item: ListItem) {
  if (isFolder(item)) {
    currentFolderId.value = item.id
    fetch(fetchingParams.value)
  } else {
    emit('open', item as CanvasRetrieve)
  }
}

function onBulkDistribution() {
  bulkUpdateDistributionDialog({
    selectedCount: tableRef.value?.selectedRowsCount || 0,
    isBulkUpdating,
    canvasType: 'template',
    selectedRows,
    onComplete: async () => {
      await onTableChange('itemsChanged')
    },
  })
}

const isDistributionEnabled = computed(() => launchDarkly.value?.enable_canvas_core_distributions)

onMounted(async () => {
  topbarRef.value = document.getElementById('slotty-ui-header')

  if (listRef.value?.$el) {
    offsetTop.value = listRef.value.$el.offsetTop || 0
  }

  try {
    isLoading.value = true

    // Initialize currentSort based on ordering if in folder-enabled mode
    if (isTemplateFolderEnabled.value) {
      await fetch(fetchingParams.value)

      // If we have a persisted sort, use it
      if (persistedSort.value) {
        currentSort.value = persistedSort.value
        const isDescending = currentSort.value.order === 'desc'
        ordering.value = isDescending ? `-${currentSort.value.field}` : currentSort.value.field
      }
      // Otherwise initialize from ordering
      else if (ordering.value) {
        const field = ordering.value.startsWith('-') ? ordering.value.substring(1) : ordering.value
        const order = ordering.value.startsWith('-') ? 'desc' : 'asc'
        currentSort.value = { field, order }
        // Save to localStorage
        persistedSort.value = currentSort.value
        syncSortingForListView()
      }
    } else {
      await fetch(fetchingParams.value)
    }

    if (launchDarkly.value.show_popularity_icons) {
      await fetchPopularityData(EntityType.TEMPLATE)
    }
    isLoading.value = false
  } catch (e) {
    console.error(e)
    isLoading.value = false
  }
})

defineExpose({
  refetch: () => fetch(fetchingParams.value),
  currentFolderId,
})
</script>
